import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Input } from "antd";
import { ThemeToggle } from "../ui/theme-toggle";
import { useTheme } from "../../contexts/ThemeContext";
import MaitaiLogo from "../../assets/maitai_logo_full.svg";
import MaitaiLogoDark from "../../assets/maitai_logo_dark_transparent.svg";
import { OnboardingService } from "../../services/OnboardingService";

type OrganizationSignupForm = {
  orgName: string;
  email: string;
};

const OrganizationSignup: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const [form] = Form.useForm<OrganizationSignupForm>();
  const { theme } = useTheme();

  useEffect(() => {
    if (localStorage.getItem("auth_mode") !== "org") {
      localStorage.setItem("auth_mode", "org");
      window.location.reload();
      return;
    }
    localStorage.removeItem("auth0_org_id");
    localStorage.removeItem("switching_org");
  }, []);

  const handleSubmit = (values: OrganizationSignupForm) => {
    setError(null);
    OnboardingService.createOrganization(
      { org_name: values.orgName.trim(), email: values.email.trim() },
      setIsSubmitting
    ).subscribe(
      (data: {
        organization_id?: string;
        invitation_id?: string;
        invitation_url?: string;
      }) => {
        const organizationId = data?.organization_id;
        const invitationId = data?.invitation_id;
        if (!organizationId || !invitationId) {
          setError("Unable to start organization signup. Please try again.");
          setIsSubmitting(false);
          return;
        }

        localStorage.setItem("auth_mode", "org");
        localStorage.setItem("auth0_org_id", organizationId);
        setSuccessEmail(values.email.trim());
      },
      (err: any) => {
        const message =
          typeof err === "string"
            ? err
            : "Unable to start organization signup. Please try again.";
        setError(message);
        setIsSubmitting(false);
      }
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative">
      <div className="absolute top-4 left-4">
        <img
          src={theme === "dark" ? MaitaiLogoDark : MaitaiLogo}
          alt="Maitai"
          className="h-8 w-auto"
        />
      </div>
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-lg space-y-6 p-8">
        {!successEmail ? (
          <div className="space-y-2 text-center">
            <h1 className="text-3xl font-gc-demi-bold text-foreground">
              Create your organization
            </h1>
            <p className="text-sm text-muted-foreground">
              Enter your organization name and work email to continue.
            </p>
          </div>
        ) : null}
        {successEmail ? (
          <div className="space-y-4 text-center">
            <h2 className="text-2xl font-gc-demi-bold text-foreground">
              Check your email
            </h2>
            <p className="text-sm text-muted-foreground">
              We sent an invitation to {successEmail}. Open the email to finish
              setting up your organization.
            </p>
            <Button
              type="primary"
              className="w-full bg-primary text-primary-foreground border-none"
              onClick={() => {
                window.location.href = "/login";
              }}>
              Back to sign in
            </Button>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label={<span className="text-foreground">Organization Name</span>}
              name="orgName"
              rules={[
                { required: true, message: "Organization name is required" },
              ]}>
              <Input
                placeholder="Acme Inc."
                className="border-border"
                style={{
                  backgroundColor: "hsl(var(--background))",
                  color: "hsl(var(--foreground))",
                }}
              />
            </Form.Item>
            <Form.Item
              label={<span className="text-foreground">Work Email</span>}
              name="email"
              rules={[
                { required: true, message: "Work email is required" },
                { type: "email", message: "Enter a valid email address" },
              ]}>
              <Input
                placeholder="you@company.com"
                className="border-border"
                style={{
                  backgroundColor: "hsl(var(--background))",
                  color: "hsl(var(--foreground))",
                }}
              />
            </Form.Item>
            {error ? (
              <Alert
                type="error"
                message={error}
                showIcon
                className="bg-card text-foreground border-border"
              />
            ) : null}
            <Button
              type="primary"
              htmlType="submit"
              className="w-full bg-primary text-primary-foreground border-none"
              loading={isSubmitting}>
              Continue
            </Button>
          </Form>
        )}
        {!successEmail ? (
          <div className="text-center">
            <Button
              type="link"
              className="text-primary"
              onClick={() => {
                window.location.href = "/login";
              }}>
              Back to sign in
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OrganizationSignup;
